import React, { createContext, useState, useEffect } from 'react';

export const CategoryContext = createContext();

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para cargar categorías desde la API
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/categories`);
      if (!response.ok) {
        throw new Error('Error al cargar categorías');
      }
      const data = await response.json();
      setCategories(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Cargar categorías al montar el componente
  useEffect(() => {
    fetchCategories();
  }, []);

  // Función para refrescar categorías (útil después de crear/editar)
  const refreshCategories = () => {
    fetchCategories();
  };

  // Función para obtener una categoría por ID
  const getCategoryById = (id) => {
    return categories.find(cat => cat._id === id);
  };

  // Función para obtener una categoría por nombre
  const getCategoryByName = (name) => {
    return categories.find(cat => cat.name === name);
  };

  // Función para obtener subcategorías de una categoría
  const getSubcategories = (categoryId) => {
    const category = getCategoryById(categoryId);
    return category?.subcategories || [];
  };

  // Función para obtener talles de una categoría o subcategoría
  const getSizes = (categoryId, subcategoryName = null) => {
    const category = getCategoryById(categoryId);
    if (!category) return [];

    if (subcategoryName) {
      const subcategory = category.subcategories?.find(sub => sub.name === subcategoryName);
      return subcategory?.sizes || category.sizes || [];
    }

    return category.sizes || [];
  };

  // Función para obtener colores de una categoría o subcategoría
  const getColors = (categoryId, subcategoryName = null) => {
    const category = getCategoryById(categoryId);
    if (!category) return [];

    if (subcategoryName) {
      const subcategory = category.subcategories?.find(sub => sub.name === subcategoryName);
      return subcategory?.colors || category.colors || [];
    }

    return category.colors || [];
  };

  // Función para convertir categorías al formato legacy (para compatibilidad)
  const getCategoriesLegacyFormat = () => {
    const legacyFormat = {};
    
    categories.forEach(category => {
      legacyFormat[category.name] = {
        icon: category.icon || 'fa-solid fa-tag',
        subcategories: category.subcategories?.map(sub => sub.name) || [],
        sizes: category.sizes || [],
        colors: category.colors || [],
        details: {}
      };

      // Agregar detalles de subcategorías
      if (category.subcategories) {
        category.subcategories.forEach(sub => {
          legacyFormat[category.name].details[sub.name] = {
            sizes: sub.sizes || category.sizes || [],
            colors: sub.colors || category.colors || []
          };
        });
      }
    });

    return legacyFormat;
  };

  const value = {
    categories,
    loading,
    error,
    refreshCategories,
    getCategoryById,
    getCategoryByName,
    getSubcategories,
    getSizes,
    getColors,
    getCategoriesLegacyFormat
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};
